import { useState, useCallback } from "react";
import { ServiceType, ServiceItem, CustomerInfo } from "../utils/tickets";
import { useServiceSelection } from "./useServiceSelection";

export interface ChangeRequestFormState {
  title: string;
  priority: string;
  description: string;

  // KE-specific fields
  selectedCustomerId: number | null;
  selectedCustomerNameDisplay: string;
  selectedServiceIds: number[];
  selectedServiceTypes: ServiceType[];
  selectedSubscriptionIds: string[];
  selectedServiceLabels: string[];

  attachmentFiles: File[];
}

export interface UseChangeRequestFormReturn {
  formState: ChangeRequestFormState;

  setTitle: (value: string) => void;
  setPriority: (value: string) => void;
  setDescription: (value: string) => void;
  setAttachmentFiles: (files: File[]) => void;

  handleCustomerSelect: (customerNameDisplay: string, uniqueCustomers: CustomerInfo[]) => void;
  handleServiceSelect: (selectedLabels: string[], filteredServiceItems: ServiceItem[]) => void;

  resetForm: () => void;
}

/**
 * The fields Change Request and Incident Report share. Who else gets the
 * response lives in `ContactDetails`, next to the modal that sends it.
 */
export const useChangeRequestForm = (): UseChangeRequestFormReturn => {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("");
  const [description, setDescription] = useState("");

  // KE-specific fields — shared with the other Kontrol Edge ticket modals
  const serviceSelection = useServiceSelection();
  const { reset: resetServiceSelection } = serviceSelection;
  const {
    selectedCustomerId,
    selectedCustomerNameDisplay,
    selectedServiceIds,
    selectedServiceTypes,
    selectedSubscriptionIds,
    selectedServiceLabels,
  } = serviceSelection;

  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  const resetForm = useCallback(() => {
    setTitle("");
    setPriority("");
    setDescription("");
    setAttachmentFiles([]);
    resetServiceSelection();
  }, [resetServiceSelection]);

  const formState: ChangeRequestFormState = {
    title,
    priority,
    description,
    selectedCustomerId,
    selectedCustomerNameDisplay,
    selectedServiceIds,
    selectedServiceTypes,
    selectedSubscriptionIds,
    selectedServiceLabels,
    attachmentFiles,
  };

  return {
    formState,
    setTitle,
    setPriority,
    setDescription,
    setAttachmentFiles,
    handleCustomerSelect: serviceSelection.handleCustomerSelect,
    handleServiceSelect: serviceSelection.handleServiceSelect,
    resetForm,
  };
};
